import { useCallback, ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { ApolloError } from '@apollo/client';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableHeader,
  EmptyDataTable,
  Button,
  Pagination,
  SearchForm,
  DownloadIcon,
  SearchFormProps,
  LoadedContent,
  IPaginationProps,
  CircularProgress,
} from '@occc/ui-design-system';
import { ScanDataStoreLevelDetailsFragment } from '@occc/graphql';

import { formatCellValue } from '../scan-data-overview.utils';

export type ScanDataStoreDetailsTableProps<T extends ScanDataStoreLevelDetailsFragment> = {
  title: string;
  data: T[] | undefined;
  additionalHeaderCells?: ReactNode | ReactNode[];
  renderAdditionalRowCells?: (row: T) => ReactNode | ReactNode[];
  loading?: boolean;
  error?: ApolloError;
  onDownloadCSV: () => void;
  isDownloading?: boolean;
  searchValue?: string;
  onSearchChange?: (searchValue: string) => void;
  paginationProps: IPaginationProps;
  isDownloadAllowed?: boolean;
};

export const ScanDataStoreDetailsTable = <T extends ScanDataStoreLevelDetailsFragment>({
  title,
  data,
  additionalHeaderCells = [],
  renderAdditionalRowCells,
  loading = false,
  error,
  onDownloadCSV,
  isDownloading = false,
  searchValue = '',
  onSearchChange,
  paginationProps,
  isDownloadAllowed = true,
}: ScanDataStoreDetailsTableProps<T>) => {
  const handleSearchSubmit: SearchFormProps['onSubmit'] = useCallback(
    ({ search }) => {
      onSearchChange?.(search ?? '');
    },
    [onSearchChange],
  );

  const renderActions = () => {
    return (
      <Box display='flex' alignItems='center' gap={1}>
        <SearchForm onSubmit={handleSearchSubmit} initialValues={{ search: searchValue }} />
        {isDownloadAllowed && (
          <Button
            variant='contained'
            startIcon={isDownloading ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={onDownloadCSV}
            disabled={isDownloading}
          >
            {isDownloading ? 'Downloading…' : 'Download CSV'}
          </Button>
        )}
      </Box>
    );
  };

  const renderEmpty = () => {
    return <EmptyDataTable testId='scan-data-store-details-table-empty' />;
  };

  const renderData = (data: T[]) => {
    return (
      <>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Site ID</TableCell>
              <TableCell>Site Name (DBA)</TableCell>
              <TableCell>Altria RCN</TableCell>
              <TableCell>RJR ACT #</TableCell>
              <TableCell>Altria Submitter ID</TableCell>
              <TableCell>RJR Submitter ID</TableCell>
              <TableCell>MSA Submitter ID</TableCell>

              {additionalHeaderCells}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item) => {
              return (
                <TableRow key={item.siteID}>
                  <TableCell>{formatCellValue(item.siteID)}</TableCell>
                  <TableCell>{formatCellValue(item.siteName)}</TableCell>
                  <TableCell align='right'>{formatCellValue(item.altriaRCN)}</TableCell>
                  <TableCell align='right'>{formatCellValue(item.rjrAccountNumber)}</TableCell>
                  <TableCell align='right'>{formatCellValue(item.altriaSubmitterID)}</TableCell>
                  <TableCell align='right'>{formatCellValue(item.rjrSubmitterID)}</TableCell>
                  <TableCell align='right'>{formatCellValue(item.msaSubmitterID)}</TableCell>

                  {renderAdditionalRowCells && renderAdditionalRowCells(item)}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Pagination {...paginationProps} />
      </>
    );
  };

  return (
    <TableContainer component={undefined}>
      <TableHeader
        disableTitleTypography
        title={<Typography variant='body1'>{title}</Typography>}
        actions={renderActions()}
      />
      <LoadedContent<T>
        loading={loading}
        error={error}
        items={data}
        renderData={renderData}
        renderEmpty={renderEmpty}
      />
    </TableContainer>
  );
};
